export const CLIENT_ERROR_APPS = [
    "tv",
    "cms",
    "guest",
    "family",
    "relative",
];
export const CLIENT_ERROR_MESSAGE_MAX = 500;
export const CLIENT_ERROR_STACK_MAX = 4000;
export function sanitizeClientPath(raw) {
    var _a;
    const path = (_a = (raw || "/").split(/[?#]/)[0]) !== null && _a !== void 0 ? _a : "/";
    return (path
        .split("/")
        .map((seg) => /^\d+$/.test(seg) || (seg.length >= 12 && /\d/.test(seg)) ? ":id" : seg)
        .join("/")
        .slice(0, 200) || "/");
}
function normaliseMessage(message) {
    return message
        .replace(/(["'`]).*?\1/g, "<str>")
        .replace(/\b[0-9a-f]{8,}\b/gi, "<hex>")
        .replace(/\d+/g, "<n>")
        .slice(0, 200);
}
function topFrame(stack) {
    if (!stack)
        return "";
    const line = stack
        .split("\n")
        .map((l) => l.trim())
        .find((l) => l.startsWith("at ") || l.includes("@"));
    if (!line)
        return "";
    return line
        .replace(/:\d+(:\d+)?\)?$/, "")
        .replace(/-[A-Za-z0-9_]{6,}\.js/, ".js")
        .replace(/\?[^\s)]*/, "")
        .slice(0, 200);
}
function fnv1a(s) {
    let h = 0x811c9dc5;
    for (let i = 0; i < s.length; i++) {
        h ^= s.charCodeAt(i);
        h = Math.imul(h, 0x01000193);
    }
    return (h >>> 0).toString(16).padStart(8, "0");
}
export function clientErrorFingerprint(app, message, stack) {
    return fnv1a(`${app}|${normaliseMessage(message)}|${topFrame(stack)}`);
}
export function describeThrown(thrown) {
    var _a, _b;
    if (thrown instanceof Error) {
        return {
            message: `${thrown.name}: ${thrown.message}`.slice(0, CLIENT_ERROR_MESSAGE_MAX),
            stack: (_a = thrown.stack) === null || _a === void 0 ? void 0 : _a.slice(0, CLIENT_ERROR_STACK_MAX),
        };
    }
    let message;
    if (typeof thrown === "string")
        message = thrown;
    else {
        try {
            message = (_b = JSON.stringify(thrown)) !== null && _b !== void 0 ? _b : String(thrown);
        }
        catch {
            message = String(thrown);
        }
    }
    return { message: (message || "(empty)").slice(0, CLIENT_ERROR_MESSAGE_MAX) };
}
export function createErrorReporter(opts) {
    var _a, _b, _c;
    const now = (_a = opts.now) !== null && _a !== void 0 ? _a : (() => Date.now());
    const dedupeMs = (_b = opts.dedupeMs) !== null && _b !== void 0 ? _b : 10 * 60000;
    const maxPerSession = (_c = opts.maxPerSession) !== null && _c !== void 0 ? _c : 20;
    const lastSent = new Map();
    let sent = 0;
    return {
        report(thrown, kind) {
            try {
                if (sent >= maxPerSession)
                    return;
                const { message, stack } = describeThrown(thrown);
                const fp = clientErrorFingerprint(opts.app, message, stack);
                const t = now();
                const prev = lastSent.get(fp);
                if (prev !== undefined && t - prev < dedupeMs)
                    return;
                lastSent.set(fp, t);
                sent++;
                opts.send({
                    app: opts.app,
                    release: opts.release.slice(0, 40) || "dev",
                    kind,
                    message,
                    ...(stack ? { stack } : {}),
                    path: sanitizeClientPath(opts.path()),
                });
            }
            catch {
            }
        },
    };
}
const ENDPOINT = "/api/client-errors";
let installed = null;
export function installClientErrorReporting(app, release) {
    if (installed)
        return installed;
    if (typeof window === "undefined" || typeof fetch !== "function")
        return null;
    const reporter = createErrorReporter({
        app,
        release,
        path: () => { var _a, _b; return (_b = (_a = window.location) === null || _a === void 0 ? void 0 : _a.pathname) !== null && _b !== void 0 ? _b : "/"; },
        send: (report) => {
            try {
                void fetch(ENDPOINT, {
                    method: "POST",
                    headers: { "content-type": "application/json" },
                    body: JSON.stringify(report),
                    keepalive: true,
                    credentials: "omit",
                }).catch(() => { });
            }
            catch {
            }
        },
    });
    window.addEventListener("error", (e) => {
        var _a;
        if (!e.error && (!e.message || e.message === "Script error."))
            return;
        reporter.report((_a = e.error) !== null && _a !== void 0 ? _a : e.message, "error");
    });
    window.addEventListener("unhandledrejection", (e) => {
        reporter.report(e.reason, "unhandledrejection");
    });
    installed = reporter;
    return reporter;
}
export function clientErrorReporter() {
    return installed;
}
